import { type ReactNode } from "react";

import { Link, type LinkProps as AriaLinkProps } from "react-aria-components";

import {
  buttonDefaultVariants,
  buttonVariants,
  linkButtonCompoundVariants,
} from "#components/v2/shared/buttonVariants";
import { useUIVersion } from "#hooks";
import { customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

const linkButtonStyles = customTv({
  base: "px-md py-xxs transition-all ease-in-out font-semibold rounded-md text-label-medium inline-flex items-center gap-x-xs text-interactive-primary-button-enabled-foreground outline-none border border-transparent no-underline data-[disabled]:cursor-not-allowed",
  variants: buttonVariants,
  compoundVariants: linkButtonCompoundVariants,
  defaultVariants: buttonDefaultVariants,
});

export interface LinkButtonProps
  extends
    Omit<AriaLinkProps, "className">,
    Omit<VariantProps<typeof linkButtonStyles>, "uiVersion"> {
  className?: string;
  IconLeft?: ReactNode;
  IconRight?: ReactNode;
}

export const LinkButton = (props: LinkButtonProps) => {
  const { variant, children, IconLeft, IconRight, ...restProps } = props;

  const uiVersion = useUIVersion();

  return (
    <Link
      {...restProps}
      className={linkButtonStyles({
        variant,
        class: props.className,
        uiVersion,
      })}
    >
      {(renderProps) => {
        if (typeof children === "function") {
          return children(renderProps);
        }

        return (
          <>
            {IconLeft}
            {children}
            {IconRight}
          </>
        );
      }}
    </Link>
  );
};
